import { reviews, faqs } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { useState, useEffect } from 'react';

export default function AboutPage() {
  const [showBackTop, setShowBackTop] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '飞飞漫画网',
    url: window.location.origin,
    description: '飞飞漫画网提供海量免费漫画在线看，免费漫画大全涵盖国漫、日漫、韩漫，无需付费即可畅读，下拉式阅读更流畅，实时更新让你免费看漫画追更无忧。',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@feifeimanga.com',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="py-10 md:py-16">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h1 className="text-3xl md:text-4xl font-heading text-foreground-950 mb-3">
                关于飞飞漫画网
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                飞飞漫画网成立于2020年，致力于为广大漫画爱好者提供海量免费漫画资源。
                我们相信，好的漫画应该让每个人都能轻松看到。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                <div className="w-14 h-14 rounded-2xl bg-accent-100 flex items-center justify-center mx-auto mb-4">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-book-read-line text-xl text-accent-600"></i>
                  </div>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">海量资源</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  收录数万部漫画作品，涵盖国漫、日漫、韩漫等多元类型，每天持续更新中。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                <div className="w-14 h-14 rounded-2xl bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-shield-check-line text-xl text-primary-600"></i>
                  </div>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">完全免费</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  全部漫画免费阅读，无需付费订阅，无隐藏收费。让漫画回归阅读的本质乐趣。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center">
                <div className="w-14 h-14 rounded-2xl bg-secondary-100 flex items-center justify-center mx-auto mb-4">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-smartphone-line text-xl text-secondary-600"></i>
                  </div>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">流畅体验</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  独创下拉式阅读模式，无需频繁翻页。PC、平板、手机全平台适配，畅快追更。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-secondary-100/50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-2">
                品牌故事
              </h2>
              <p className="text-sm text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                从一群漫画爱好者的初心出发，飞飞漫画网始终秉持"让每个人都能免费看好漫画"的理念。
              </p>
            </div>
            <div className="max-w-3xl mx-auto">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  飞飞漫画网的创始团队是一群资深的漫画爱好者。在创立这个平台之前，我们深知漫画迷们在寻找高质量免费漫画时所面临的困难——资源分散、画质参差不齐、阅读体验糟糕。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  于是，我们决定打造一个真正属于漫画爱好者的平台。飞飞漫画网从2020年起步，经过多年的积累和优化，如今已经成长为国内领先的免费漫画阅读平台之一，日均服务数百万漫画读者。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们独创的下拉式阅读模式，让读者只需滑动屏幕即可流畅阅读整话漫画，告别频繁点击翻页的烦恼。同时，我们与众多漫画平台保持紧密合作，确保热门漫画第一时间更新，让读者追更无忧。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-2">
                读者口碑
              </h2>
              <p className="text-sm text-foreground-500">来自真实读者的评价</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reviews.map((review) => (
                <div key={review.id} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img src={review.avatar} alt={review.user} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{review.user}</p>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }, (_, i) => (
                          <div key={i} className="w-3 h-3 flex items-center justify-center">
                            <i className={`text-xs ${i < review.rating ? 'text-accent-500 ri-star-fill' : 'text-foreground-200 ri-star-line'}`}></i>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                  <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-2">
                常见问题
              </h2>
            </div>
            <div className="max-w-2xl mx-auto flex flex-col gap-2">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                  >
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                    <div className={`w-5 h-5 flex-shrink-0 flex items-center justify-center transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-foreground-400"></i>
                    </div>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaq === idx ? 'max-h-40 pb-4' : 'max-h-0'
                    }`}
                  >
                    <p className="px-5 text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-6">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-2">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500">有任何问题或建议？欢迎随时联系我们</p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-mail-line text-primary-600"></i>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-800">contact@feifeimanga.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-customer-service-line text-accent-600"></i>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">客服QQ</p>
                      <p className="text-sm font-medium text-foreground-800">800123456</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-time-line text-secondary-600"></i>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">工作时间</p>
                      <p className="text-sm font-medium text-foreground-800">每天 9:00 - 22:00</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-background-200 flex items-center justify-center flex-shrink-0">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-wechat-line text-foreground-600"></i>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">微信公众号</p>
                      <p className="text-sm font-medium text-foreground-800">飞飞漫画网</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-8 right-8 z-40 w-10 h-10 rounded-full bg-primary-500 text-background-50 flex items-center justify-center shadow-lg hover:bg-primary-600 transition-all duration-300 cursor-pointer ${
          showBackTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        aria-label="回到顶部"
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-up-line text-lg"></i>
        </div>
      </button>
    </>
  );
}